import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RxFormBuilder } from '@rxweb/reactive-form-validators';
import { ToastyService } from 'ng2-toasty/src/toasty.service';
import { AgentRoutes } from '../../../../abstration/control-agent-routes';
import { ReactiveFormsValidator } from '../../../../abstration/forms-validator';
import { FileUploadHelper } from '../../../../helpers/file-upload-helper';
import { FormHelper } from '../../../../helpers/form-helper';
import { AgentRegister } from '../../../../models/agents/agent-register';
import { AgentType } from '../../../../models/enums/agent-type-enum';
import { PersonType } from '../../../../models/enums/person-type.enum';
import { ServicesTypes } from '../../../../models/enums/services.enum';
import { WizardCompanyEnum } from '../../../../models/enums/wizard-company.enum';
import { FileUploaderComponent } from '../../../shared/file-uploader/file-uploader.component';
import { BaseModel } from '../investor-representatives-data/personas/base.model';
import { InvestmentFundInvestorDataFormGroup } from './forms/investment-fund-form-group';
import { InvestorCompanyInvestorDataFormGroup } from './forms/investor-company.form-group';
import { ManagerInvestorDataFormGroup } from './forms/manager.form-group';
import { InvestorCompanyModel } from './personas/investor-company.model';
import { InvestorFundModel } from './personas/investor-fund.model';
import { IssuerModel } from './personas/issuer-company.model';
import { ManagerModel } from './personas/manager.model';

export abstract class InvestorDataControlRegisterSections extends ReactiveFormsValidator {
  public investorCompany: InvestorCompanyModel;
  public manager: ManagerModel;
  public investmentFund: InvestorFundModel;
  public issuer: IssuerModel;

  public abstract formGroup: FormGroup;
  public abstract initReactiveForms(): void;

  constructor(
    protected formBuilder: FormBuilder,
    protected toastyService: ToastyService,
    protected investorFundFormGroup: InvestorCompanyInvestorDataFormGroup,
    protected InvestmentFundInvestorDataFormGroup: InvestmentFundInvestorDataFormGroup,
    protected ManagerInvestorDataFormGroup: ManagerInvestorDataFormGroup,
    protected rxFormBuilder: RxFormBuilder
  ) {
    super(formBuilder, toastyService);
  }

  fieldAvailableForAgent(
    agent: AgentRegister,
    field: string,
    section: string
  ): boolean {
    return this.showFormControlName(field);
  }

  sectionAvaliableForAgent(
    agent: AgentRegister,
    sectionName: string,
    section: string
  ): boolean {
    if (!agent) {
      return false;
    }

    switch (agent.personType) {
      case PersonType.Juridica:
        return this.InvestmentFundInvestorDataFormGroup.companySectionsForAgents(
          section,
          agent.entitiesType
        ).includes(sectionName);
      case PersonType.PJInvestidor:
        return this.investorFundFormGroup
          .companySectionsForAgents(section, agent.entitiesType)
          .includes(sectionName);
      case PersonType.Gestor:
        return this.ManagerInvestorDataFormGroup.companySectionsForAgents(
          section,
          agent.entitiesType
        ).includes(sectionName);
      default:
        break;
    }
  }

  validateDocumentsByPerson(
    agent: AgentRegister,
    fileUploaderFoundRulet: FileUploaderComponent,
    fileUploaderCorporate: FileUploaderComponent,
    fileUploaderDueDiligence: FileUploaderComponent,
    fileInternalPolicies: FileUploaderComponent,
    fileUploaderAuditedFinancialStatements: FileUploaderComponent
  ) {
    const filesPJInvestidor = [fileUploaderCorporate];

    if (agent.isEscrowPartesRelacionadas() || agent.isEscrowTitular()) {
      filesPJInvestidor.push(fileUploaderAuditedFinancialStatements);
    }

    switch (agent.personType) {
      case PersonType.Juridica:
        return FileUploadHelper.CheckUploadRequirement([
          fileUploaderFoundRulet,
          fileUploaderCorporate
        ]);
      case PersonType.PJInvestidor:
        return FileUploadHelper.CheckUploadRequirement(filesPJInvestidor);
      case PersonType.Gestor:
        return FileUploadHelper.CheckUploadRequirement([
          fileUploaderCorporate,
          fileUploaderDueDiligence,
          fileInternalPolicies
        ]);
      case PersonType.Emissor:
        return FileUploadHelper.CheckUploadRequirement([
          fileUploaderCorporate,
          fileUploaderDueDiligence,
          fileInternalPolicies
        ]);
      default:
        return { hasAnyEmpty: false, title: null };
    }
  }

  getCompanyFormGroupInvestorData(agent: AgentRegister): FormGroup {
    const formHelper = new FormHelper();
    let form: FormGroup;
    switch (agent.personType) {
      case PersonType.PJInvestidor:
        this.investorCompany = new InvestorCompanyModel(agent.personType);
        form = formHelper.ExtractFormGroupFromModel(
          this.investorCompany,
          InvestorCompanyModel
        );
        return this.filterFieldsByProduct(form, agent);
      case PersonType.Juridica:
        this.investmentFund = new InvestorFundModel(agent.personType);
        form = formHelper.ExtractFormGroupFromModel(
          this.investmentFund,
          InvestorFundModel
        );
        return form;
      case PersonType.Gestor:
        this.manager = new ManagerModel(agent.personType);
        form = formHelper.ExtractFormGroupFromModel(this.manager, ManagerModel);
        return form;
      case PersonType.Emissor:
        this.issuer = new IssuerModel(agent.personType);
        form = formHelper.ExtractFormGroupFromModel(this.issuer, IssuerModel);
        return form;
      default:
        break;
    }
  }

  filterFieldsByProduct(form: FormGroup, agent: AgentRegister) {
    if (
      agent.services &&
      agent.services.length === 1 &&
      agent.services[0].description === ServicesTypes.FUNDOS
    ) {
      form.removeControl('patrimonialSituationAmount');
      form.removeControl('annualBilling');

      return form;
    }

    const isEscrow =
      agent.isEscrowTitular() || agent.isEscrowPartesRelacionadas();

    if (!isEscrow) {
      form.removeControl('email');
    }

    if (isEscrow) {
      form.controls['email'].setValidators([Validators.required]);
    }

    return form;
  }

  showUploadFileComponent(agent: AgentRegister, uploadCategoryType: string) {
    let entity: BaseModel;

    if (agent.personType === PersonType.PJInvestidor) {
      entity = this.investorCompany;
    } else if (agent.personType === PersonType.Juridica) {
      entity = this.investmentFund;
    }
    if (agent.personType === PersonType.Gestor) {
      entity = this.manager;
    } else if (agent.personType === PersonType.Emissor) {
      entity = this.issuer;
    }

    return entity.allowedFiles.some((file) => file === uploadCategoryType);
  }

  // to do: remove this logic, because it's duplicated in others components
  getAllRoutesFromAgent(agent: AgentRegister): any {
    const routes = agent.entitiesType.map(
      (agentType) => AgentRoutes[agentType]
    );
    const uniqueRoutes = [...new Set([].concat(...routes))];
    const sortedUniqueRoutes = Object.values(WizardCompanyEnum).filter(
      (route) => uniqueRoutes.includes(route)
    );
    return sortedUniqueRoutes;
  }

  getNextStepRoute(
    agent: AgentRegister,
    actualRoute: WizardCompanyEnum
  ): WizardCompanyEnum {
    let routes = this.getAllRoutesFromAgent(agent);
    routes = routes.filter((route) =>
      this.skipCompanySteps(route, new AgentRegister(agent))
    );
    const nextRouteIndex = routes.indexOf(actualRoute) + 1;
    return routes[nextRouteIndex];
  }

  getPreviousStepRoute(
    agent: AgentRegister,
    actualRoute: WizardCompanyEnum
  ): WizardCompanyEnum {
    let routes = this.getAllRoutesFromAgent(agent);
    routes = routes.filter((rt) => this.skipCompanySteps(rt, agent));
    const previousRouteIndex = routes.indexOf(actualRoute) - 1;
    return routes[previousRouteIndex];
  }

  skipCompanySteps(step: string, agent: AgentRegister) {
    const skipPartner = step !== WizardCompanyEnum.Partner;
    const skipFiscalData = step !== WizardCompanyEnum.FiscalData;
    const isInvestmentFund = agent.personType === PersonType.Juridica;
    const skipBeneficiaries = step !== WizardCompanyEnum.Beneficiaries;
    const skipContract = step !== WizardCompanyEnum.Contract;
    const skipPaymentAccountOpening =
      step !== WizardCompanyEnum.PaymentAccountOpening;
    const skipSuitability = step !== WizardCompanyEnum.Suitability;
    const skipFinancialInformation =
      step !== WizardCompanyEnum.FinancialInformation;
    const skipAssetPortfolio = step !== WizardCompanyEnum.AssetPortfolio;
    const skipAuthorizations = step !== WizardCompanyEnum.Authorizations;
    const isPJ = agent.personType === PersonType.PJInvestidor;

    const isEscrow =
      agent.isEscrowPartesRelacionadas() || agent.isEscrowTitular();

    const isBancoLiquidante =
      agent.isBancoLiquidantePartesRelacionadas() ||
      agent.isBancoLiquidanteTitular();

    const isfundo = agent.isFundo();

    if (isInvestmentFund) {
      return (
        skipPartner &&
        skipFiscalData &&
        skipBeneficiaries &&
        skipContract &&
        skipPaymentAccountOpening &&
        skipAuthorizations
      );
    }

    if (isPJ && (isEscrow || isBancoLiquidante) && !isfundo) {
      if (
        agent.isEscrowPartesRelacionadas() ||
        agent.isBancoLiquidantePartesRelacionadas()
      ) {
        return (
          skipBeneficiaries &&
          skipSuitability &&
          skipFinancialInformation &&
          skipAssetPortfolio &&
          skipContract &&
          skipPaymentAccountOpening
        );
      }
      return skipSuitability && skipFinancialInformation && skipAssetPortfolio;
    }

    return true;
  }

  getCurrentStepRoute(
    agent: AgentRegister,
    actualRoute: WizardCompanyEnum
  ): WizardCompanyEnum {
    const routes = this.getAllRoutesFromAgent(agent);
    const currentRouteIndex = routes.indexOf(actualRoute);
    if (currentRouteIndex === -1) {
      const firstRoute = AgentRoutes[AgentType.Shareholder][0];
      return firstRoute;
    }
    return routes[currentRouteIndex];
  }
}
